import { createSpawnSourceReaders, type SpawnSourcePackage } from '../spawn/source-plan';
import type { Row } from '../spawn/row-plan';
import { readItemField } from '../compile/item-field-reader';
import { readInventoryField, readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { surfaceField } from '../compile/from-base-encounter-media';
import { fromBaseRecord, fromBaseUuid } from '../compile/from-base-context';
import { lookupRandomTableRange, randomTableMaxEnd, rollRandomTablePools } from '../rolls/random-table';
import { phpJsonInt, phpJsonTruthy } from '../compile/php-json-values';
import { floorAt } from '../../../../ft3d/engine/ft3d-shape-core.js';
import { generatorScatter } from './generator-scatter';

export type ResourceFamily = 'herbal_plant' | 'food_plant' | 'tree' | 'ore_vein';
export interface ResourceInputs extends SpawnSourcePackage {
    schema: 'resource-generator-inputs.v1'; destination: Row; location: Row; parent_location: Row;
}
export interface ResourceCommand {
    schema: 'random-generator.v1'; destination_id: string; operation_id: string;
    rows: { kind: 'inventory' | 'status'; row: Row }[]; summary: string;
}
export const ORE_TABLES = {
    '19817552-D32A-4B97-B404-745927E1B764': '96746EF0-E436-CF7F-F471-6A0CA906751A',
    'FD2AC285-7609-49D5-B6BD-3287F920472F': 'DD97A66A-E0AE-54F4-37EC-48725D4EEACC',
    '7EA716CF-ABCE-CD3C-0B9E-2310BB8C9DD8': '52F7255A-4136-BFF0-C973-0C4B27B15FFA',
    '1A7C5177-77E3-F1A5-0423-D09C137CA2A4': '96AD76EE-C674-A256-41C8-CA1697DCDFF9',
    '38FB8933-47F6-4A79-60CF-14E80BCF9BF5': '10B816F0-D2D0-468F-8CFE-F5DEEF363095',
} as const;
const upper = (value: unknown): string => readLayerListEntryUuid(value).toUpperCase();
const field = readItemField;

/** One immutable command, including all descendants, before any storage request. */
export function planResourceGenerator(family: ResourceFamily, values: Row, input: ResourceInputs, encounter: Row,
    destination: string, random: { uuid(): string; die(sides: number): number }): ResourceCommand {
    const read = createSpawnSourceReaders(input, encounter, random.uuid, random.die);
    let environment = upper(surfaceField(input.destination, 'encounter_environment') ?? surfaceField(input.location, 'location_environment'));
    const parentEnvironment = upper(surfaceField(input.parent_location, 'location_environment'));
    if (family === 'ore_vein' && environment === '38FB8933-47F6-4A79-60CF-14E80BCF9BF5' && parentEnvironment)
        environment = parentEnvironment;
    const explicit = upper(values[`random_${family}`]);
    const catalog = fromBaseRecord(input.base.catalog) ? input.base.catalog : {};
    const source = (id: string): Row => {
        const row = read.item(id);
        if (field(row, 'item_type') !== family) throw new Error('resource-generator:wrong-resource-kind');
        return row;
    };
    const costAllowed = (row: Row): boolean => {
        const cost = Number(read.value(row, 'cost_gp') ?? 0), min = phpJsonInt(values.random_generator_cost_gp_min), max = phpJsonInt(values.random_generator_cost_gp_max);
        return cost <= 0 || ((!min || cost >= min) && (!max || cost <= max));
    };
    const pool = (): Row[] => {
        const all = Object.values(catalog).filter((row): row is Row => fromBaseRecord(row)
            && field(row, 'item_type') === family && !phpJsonInt(field(row, 'item_deleted')) && !phpJsonInt(field(row, 'item_archived')));
        const biome = phpJsonTruthy(values.random_generator_constrain_to_environment_type) && environment
            ? all.filter(row => read.entries(row, 'creature_environment').some(entry => upper(entry) === environment)) : [];
        // The existing resource picker prefers a biome match and still supplies a resource when none is authored.
        return biome.length ? biome : all;
    };
    const ore = (): Row => {
        const id = ORE_TABLES[environment as keyof typeof ORE_TABLES] ?? ORE_TABLES['38FB8933-47F6-4A79-60CF-14E80BCF9BF5'];
        const buckets = input.base.random_table_rows;
        const saved = fromBaseRecord(buckets) ? buckets[id] : null;
        if (!Array.isArray(saved)) throw new Error(`resource-generator:ore-table-missing:${id}`);
        const ranges = (saved as Row[]).filter(row => !phpJsonInt(field(row, 'random_table_range_deleted')))
            .sort((a, b) => phpJsonInt(field(b, 'roll_range_end')) - phpJsonInt(field(a, 'roll_range_end'))
                || phpJsonInt(field(b, 'random_table_range_i')) - phpJsonInt(field(a, 'random_table_range_i')))
            .map(row => ({ begin: phpJsonInt(field(row, 'roll_range_begin')), end: phpJsonInt(field(row, 'roll_range_end')),
                attached: upper(field(row, 'item_id_attached')) }));
        const maximum = randomTableMaxEnd(ranges);
        if (maximum < 1) throw new Error(`resource-generator:ore-table-empty:${id}`);
        const attached = lookupRandomTableRange(ranges, random.die(maximum))?.attached;
        if (!attached) throw new Error(`resource-generator:ore-table-no-result:${id}`);
        return source(attached);
    };
    const options = !fromBaseUuid(explicit) && family !== 'ore_vein' ? pool() : [];
    const pick = (): Row => {
        if (fromBaseUuid(explicit)) return source(explicit);
        if (family !== 'ore_vein') {
            if (!options.length) throw new Error(`resource-generator:empty-pool:${family}`);
            return source(String(field(options[random.die(options.length) - 1]!, 'item_id')));
        }
        return ore();
    };
    const dice = phpJsonInt(values.random_generator_dice), die = phpJsonInt(values.random_generator_die);
    const rolled = dice && die ? rollRandomTablePools([{ dice, die, mod: phpJsonInt(values.random_generator_mod) }], 0, random.die) : null;
    if (dice && die && !rolled) throw new Error('resource-generator:invalid-dice');
    const quantity = rolled?.roll_result ?? 1;
    const rows: ResourceCommand['rows'] = [];
    const shapeValue = surfaceField(encounter, 'encounter_shape');
    const shape = typeof shapeValue === 'string' ? JSON.parse(shapeValue) as Row : fromBaseRecord(shapeValue) ? shapeValue : null;
    const scatter = phpJsonTruthy(values.random_generator_scatter) ? generatorScatter(encounter,
        [[input.destination, 'encounter'], [input.location, 'location']], random.die, read.value, () => (random.die(61) - 31) / 10) : null;
    let created = 0;
    for (let i = 0; i < quantity; i++) {
        const item = pick();
        if (!costAllowed(item)) continue;
        const plan = read.inventory(item, { encounter_creatures: { encounter_creature_id: destination } }, 1, '', true);
        const x = scatter || shape ? 0 : (random.die(61) - 31) / 10;
        for (const write of plan.writes) {
            if (write.op !== 'insert' || !['inventory', 'statuses'].includes(write.table)) throw new Error(`resource-generator:unsupported-write:${write.table}`);
            const row = { ...write.row };
            if (write.table === 'inventory') {
                delete row.inventory_private; delete row.inventory_deleted;
                if (readInventoryField({ inventory: row }, 'possess_id') === destination) {
                    Object.assign(row, scatter ? scatter() : { x_pos: x, y_pos: 0, z_pos: shape ? floorAt(shape, x, 0) ?? 0 : 0 });
                    created++;
                }
            } else { delete row.status_private; delete row.status_deleted; delete row.status_campaign_id; }
            rows.push({ kind: write.table === 'inventory' ? 'inventory' : 'status', row });
        }
    }
    return { schema: 'random-generator.v1', destination_id: destination, operation_id: random.uuid(), rows,
        summary: `Generated ${created} ${family.replace(/_/g, ' ')} object(s).` };
}
